/**
 * AtlasHerdsModule — the grazing herds of the ambient life wave (2026-09-29): copperhorns on the regolith plains,
 * fenbacks in the moss wetland, woolcrags in the rocky highlands. Rules: `lib/ambient_herds.ts` (pure). Bodies and
 * the procedural legs / neck: `lib/ambient_models.ts`.
 *
 * - WHERE is the world seed's: a herd per 1.6 km cell where its biome's open ground allows one, wandering a slow loop on
 *   the WALL clock — every client sees the same herd in the same place. Only the flight from a person is per client.
 * - DRAWN from 60 m out to min(2 km, the tier's terrain window) — past the window the far tier's ground is drawn
 *   deliberately LOW, and a herd there would float. Two LODs (LOD1 inside 220 m, LOD2 beyond), one instanced draw each per
 *   species, frustum-culled by the herd's extent; the tier's `particleCap` caps the animals (8 … 120), nearest herds first.
 * - THE FRAME: the batches hang under an anchor group near the viewer (absolute coordinates, re-seated past 1.5 km), so
 *   float32 never sees a 900 km coordinate.
 * - Under the `ambientLife` switch like the rest of ambient life: off = every herd gone at once. Off in VR (xr_profile).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import type { AssetModule } from './AssetModule.js';
import { forestDensity } from '../lib/forest_field.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { lakeSurfaceAtBaked } from '../lib/lakes.js';
import { roadSurfaceMask, stampedHeight } from '../lib/terrain_stamp.js';
import { biomeAt, riverWaterAt, PLATEAU_RADIUS } from '../../../shared/terrain.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { activePreset } from '../lib/quality_tiers.js';
import { sfxLoop, stopSfxScope, type LoopHandle } from '../lib/sfx.js';
import { CHUNK_SIZE } from '../config.js';
import {
  HERD, HERD_SPECIES, HERD_SPECIES_KEYS, herdCandidate, herdCellsNear, herdSpeciesFor, herdSize, herdCentre, herdSlot, makeGrazer,
  stepGrazer, stepFlight, herdAnimalCap, herdRng, type HerdSite, type HerdSpeciesKey, type Grazer, type HerdFlight, type HerdThreat, type HerdGround,
} from '../lib/ambient_herds.js';
import { creatureParts, CreatureBatch } from '../lib/ambient_models.js';

const REANCHOR_M = 1500;
/** Re-scan the cells around the viewer this often (s). */
const SCAN_EVERY_S = 2;
/** LOD1 inside this (m), LOD2 beyond. */
const LOD1_M = 220;
/** Per-animal ground re-sample after it moved this far (m). */
const GROUND_EVERY_M = 0.75;
/** The fleeing hooves are heard inside this (m). */
const HOOVES_M = 260;
const SCOPE = 'ambient-herds';

interface Herd {
  site: HerdSite;
  species: HerdSpeciesKey;
  count: number;
  animals: Grazer[];
  /** Each animal's resting slot around the centre — computed once (herdSlot builds an rng; never per frame). */
  slots: Array<{ ox: number; oz: number }>;
  /** Per-animal cached ground (y, and where it was sampled). */
  gy: Float64Array; gx: Float64Array; gz: Float64Array;
  flight: HerdFlight;
  rng: () => number;
  dist: number;
  /** Placed by the probe lever, not the seeded field. */
  forced: boolean;
  /** Probe pictures only: ignores people (a close-up of grazers needs them not to run). */
  tame: boolean;
}

interface ModelSlot { batch: CreatureBatch | null; loading: boolean; failed: number; retryAt?: number }

export class AtlasHerdsModule implements IModule {
  readonly name = 'AtlasHerds';
  private app: App | null = null;
  private scene: THREE.Scene | null = null;
  private env: EnvironmentModule | null = null;
  private assets: AssetModule | null = null;
  private seed = '';
  private sea = 0;
  private sites: SettlementSite[] = [];
  private readonly herds = new Map<string, Herd>();
  /** Cells judged empty, with why (bounded — cleared past 600). */
  private readonly rejected = new Map<string, string>();
  private readonly models = new Map<string, ModelSlot>();
  private anchor: THREE.Group | null = null;
  private anchorX = 0; private anchorY = 0; private anchorZ = 0;
  private scanIn = 0;
  private lastMs = 0;
  private gate: string | null = 'not started';
  private hooves: LoopHandle | null = null;
  private disposed = false;
  /** The far (LOD2) bodies are asked for as soon as the layer is live — see `prefetch`. */
  private prefetched = false;
  /** Probe lever: draw every animal at this LOD (null = by distance). */
  private forceLod: 1 | 2 | null = null;
  private readonly stats = { scans: 0, herdsMade: 0, drawn: 0, lod1: 0, lod2: 0, fleeing: 0 };
  /** The animals drawn THIS frame (absolute XZ + body length) — the trample field reads them (physics follow-up A). */
  private readonly drawnX: number[] = [];
  private readonly drawnZ: number[] = [];
  private readonly drawnLen: number[] = [];

  /** READ-ONLY: the herd animals drawn this frame within `r` m (square) of (x, z) — what bends the grass under a herd. */
  drawnAnimalsNear(x: number, z: number, r: number): Array<{ x: number; z: number; lengthM: number }> {
    const out: Array<{ x: number; z: number; lengthM: number }> = [];
    for (let i = 0; i < this.drawnX.length; i++) {
      if (Math.abs(this.drawnX[i] - x) <= r && Math.abs(this.drawnZ[i] - z) <= r) out.push({ x: this.drawnX[i], z: this.drawnZ[i], lengthM: this.drawnLen[i] });
    }
    return out;
  }

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene as THREE.Scene;
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:environment-updated', () => this.onWorld());
    app.world.addSystem(makeSystem('AtlasHerds', Phase.Animation, 43, (dt) => this.tick(dt)));
    const g = globalThis as unknown as Record<string, unknown>;
    g.__herds = () => this.diag();
    /**
     * Probe lever: a herd of `species` `dist` metres ahead of the viewer and `side` to its right, now (the flag and a
     * particle budget still apply). `tame` = it ignores people — for a close-up picture only.
     */
    g.__herdsSpawnNear = (species: HerdSpeciesKey = 'copperhorn', dist = 160, side = 0, tame = false) => this.spawnNear(species, dist, side, tame);
    /** Probe lever: the nearest seeded herd home within `maxKm` of the viewer (null when none) — for a teleport. */
    g.__herdsFind = (maxKm = 25) => this.findNearest(maxKm);
    /** Probe lever: draw every animal at LOD 1 or 2 (null = by distance, the default). */
    g.__herdsLod = (lod: 1 | 2 | null = null) => { this.forceLod = lod === 1 || lod === 2 ? lod : null; return this.forceLod; };
  }

  private onWorld(): void {
    let seed = '';
    try { const conn = atlas.conn, id = atlas.currentWorldId; if (conn && id !== null) { const w = conn.db.world.worldId.find(id); if (w && w.kind === 'commons') seed = w.terrainSeed; } } catch { /* churn */ }
    if (seed === this.seed) return;
    this.clearHerds();
    this.rejected.clear();
    this.seed = seed;
    this.sea = seed ? commonsSeaLevel(seed) : 0;
    try { this.sites = seed ? settlementSites(seed) : []; } catch { this.sites = []; }
  }

  // ─── the field ─────────────────────────────────────────────────────────────
  private groundOf(x: number, z: number): HerdGround {
    const seed = this.seed;
    const h = stampedHeight(seed, x, z);
    const lake = lakeSurfaceAtBaked(x, z), river = riverWaterAt(seed, x, z);
    const wet = (lake !== null && h < lake) || (river !== null && h < river + 0.5);
    let toTown = Infinity;
    for (const s of this.sites) { const d = Math.hypot(s.centerX - x, s.centerZ - z) - PLATEAU_RADIUS[s.tier]; if (d < toTown) toTown = d; }
    let lo = h, hi = h;
    for (const [dx, dz] of [[40, 0], [-40, 0], [0, 40], [0, -40]]) { const v = stampedHeight(seed, x + dx, z + dz); if (v < lo) lo = v; if (v > hi) hi = v; }
    return { biome: biomeAt(seed, x, z), forestDensity: forestDensity(seed, x, z, this.sea), aboveSea: h - this.sea, wet, toTown, road: roadSurfaceMask(seed, x, z), relief: hi - lo };
  }

  /** The herd of a cell, or null (remembered with its reason). */
  private judgeCell(cx: number, cz: number): Herd | null {
    const id = `${cx}:${cz}`;
    const known = this.herds.get(id);
    if (known) return known;
    if (this.rejected.has(id)) return null;
    const cand = herdCandidate(this.seed, cx, cz);
    if (!cand) { this.remember(id, 'no herd in this cell'); return null; }
    const verdict = herdSpeciesFor(this.groundOf(cand.homeX, cand.homeZ));
    if (!verdict.species) { this.remember(id, verdict.why); return null; }
    return this.makeHerd(cand, verdict.species, false);
  }

  private remember(id: string, why: string): void {
    if (this.rejected.size > 600) this.rejected.clear();
    this.rejected.set(id, why);
  }

  private makeHerd(site: HerdSite, species: HerdSpeciesKey, forced: boolean, tame = false): Herd {
    const sp = HERD_SPECIES[species];
    const count = herdSize(sp, site.count);
    const c = herdCentre(site, Date.now() / 1000);
    const animals: Grazer[] = [];
    for (let i = 0; i < count; i++) animals.push(makeGrazer(site, sp, i, c.x, c.z));
    const slots = Array.from({ length: count }, (_, i) => herdSlot(site, sp, i));
    const h: Herd = {
      site, species, count, animals, slots, gy: new Float64Array(count), gx: new Float64Array(count).fill(NaN), gz: new Float64Array(count).fill(NaN),
      flight: { ox: 0, oz: 0, fleeing: false, calm: HERD.calmS }, rng: herdRng(site.seed ^ 0x5bd1e995), dist: Infinity, forced, tame,
    };
    this.herds.set(site.id, h);
    this.stats.herdsMade++;
    return h;
  }

  private drawRange(): number {
    const cr = activePreset().chunkRadius ?? 3;
    return Math.min(HERD.drawM, (cr + 0.5) * CHUNK_SIZE);
  }

  private scan(px: number, pz: number): void {
    this.stats.scans++;
    const range = this.drawRange();
    for (const [cx, cz] of herdCellsNear(px, pz, range + HERD.wander + 200)) this.judgeCell(cx, cz);
    for (const [id, h] of this.herds) if (!h.forced && Math.hypot(h.site.homeX - px, h.site.homeZ - pz) > HERD.despawnM + range * 0.2) this.herds.delete(id);
  }

  // ─── the tick ──────────────────────────────────────────────────────────────
  private tick(dtRaw: number): void {
    if (!this.seed && atlas.currentWorldId !== null) this.onWorld();
    const app = this.app;
    if (app) {
      this.env ??= (app.getModule?.('Environment') as EnvironmentModule | undefined) ?? null;
      this.assets ??= (app.getModule?.('AssetModule') as AssetModule | undefined) ?? null;
    }
    const p = activePreset();
    const cap = herdAnimalCap(p.particleCap ?? 0);
    this.gate = p.ambientLife === false ? 'ambientLife off'
      : cap <= 0 ? 'particleCap 0'
        : !this.seed ? 'no world seed yet'
          : atlas.loadingGate ? 'loading gate is up'
            : atlas.currentWorldId !== atlas.commonsWorldId ? 'not in the Commons'
              : atlas.spaceFlight ? 'in space flight'
                : !this.env ? 'modules not resolved yet'
                  : null;
    this.drawnX.length = 0; this.drawnZ.length = 0; this.drawnLen.length = 0; // this frame's drawn animals (the trample feed)
    if (this.gate) { this.clearHerds(); return; }
    // AMBIENT TIME: the wall clock (≤ 0.5 s a frame), never the engine's clamped dt (the fauna's law)
    const now = performance.now();
    const dt = Math.min(0.5, Math.max(0, this.lastMs ? (now - this.lastMs) / 1000 : dtRaw));
    this.lastMs = now;
    const px = atlas.localX, py = atlas.localY, pz = atlas.localZ;
    this.scanIn -= dt;
    if (this.scanIn <= 0) { this.scanIn = SCAN_EVERY_S; this.scan(px, pz); }
    this.ensureAnchor(px, py, pz);
    this.prefetch();
    const threats = this.threats(px, pz);
    const tS = Date.now() / 1000;
    const range = this.drawRange();
    // nearest herds first, under the tier's animal cap
    const list = [...this.herds.values()];
    for (const h of list) { const c = herdCentre(h.site, tS); h.dist = Math.hypot(c.x + h.flight.ox - px, c.z + h.flight.oz - pz); }
    list.sort((a, b) => a.dist - b.dist);
    for (const slot of this.models.values()) slot.batch?.begin();
    let budget = cap, drawn = 0, lod1 = 0, lod2 = 0, fleeing = 0;
    let hoofGain = 0, hoofAt: { x: number; y: number; z: number } | null = null;
    const hidden = atlas.interiorId !== null;
    for (const h of list) {
      if (h.dist > range + HERD.wander) continue;
      const sp = HERD_SPECIES[h.species];
      const c = herdCentre(h.site, tS);
      const near = h.tame ? null : this.nearestThreat(threats, c.x + h.flight.ox, c.z + h.flight.oz);
      h.flight = stepFlight(h.flight, sp, dt, c.x, c.z, near);
      if (h.flight.fleeing) fleeing++;
      const n = Math.min(h.count, budget);
      budget -= n;
      for (let i = 0; i < h.count; i++) {
        const g = h.animals[i];
        const s = h.slots[i];
        const nearI = h.tame ? null : this.nearestThreat(threats, g.x, g.z);
        stepGrazer(g, sp, dt, c.x + h.flight.ox + s.ox, c.z + h.flight.oz + s.oz, h.flight.fleeing, nearI, h.rng);
        if (i >= n) continue;
        const d = Math.hypot(g.x - px, g.z - pz);
        if (d > range) continue;
        const y = this.animalGround(h, i, g);
        const lod = this.forceLod ?? (d < LOD1_M ? 1 : 2);
        const batch = this.batchFor(h.species, lod);
        if (!batch) continue;
        if (batch.put(g.x - this.anchorX, y - this.anchorY, g.z - this.anchorZ, g.yaw, sp.lengthM, g.gait, g.gaitPrev, g.neck, g.swing)) {
          drawn++; if (lod === 1) lod1++; else lod2++;
          if (!hidden) { this.drawnX.push(g.x); this.drawnZ.push(g.z); this.drawnLen.push(sp.lengthM); }
        }
      }
      if (h.flight.fleeing && h.dist < HOOVES_M) {
        const gain = 0.5 * Math.min(1, h.count / 10) * (1 - h.dist / HOOVES_M);
        if (gain > hoofGain) { hoofGain = gain; hoofAt = { x: c.x + h.flight.ox, y: this.env!.groundAt(c.x + h.flight.ox, c.z + h.flight.oz) + 0.5, z: c.z + h.flight.oz }; }
      }
    }
    for (const slot of this.models.values()) slot.batch?.end(hidden);
    this.stats.drawn = drawn; this.stats.lod1 = lod1; this.stats.lod2 = lod2; this.stats.fleeing = fleeing;
    this.updateHooves(hoofGain, hoofAt);
  }

  private threats(px: number, pz: number): HerdThreat[] {
    const out: HerdThreat[] = [{ x: px, z: pz }];
    for (const r of atlas.remotes.values()) if (Number.isFinite(r.targetX) && Number.isFinite(r.targetZ)) out.push({ x: r.targetX, z: r.targetZ });
    return out;
  }

  private nearestThreat(ts: HerdThreat[], x: number, z: number): HerdThreat | null {
    let best: HerdThreat | null = null, bd = Infinity;
    for (const t of ts) { const d = Math.hypot(t.x - x, t.z - z); if (d < bd) { bd = d; best = t; } }
    return best;
  }

  private animalGround(h: Herd, i: number, g: Grazer): number {
    if (!(Math.hypot(g.x - h.gx[i], g.z - h.gz[i]) < GROUND_EVERY_M)) {
      h.gy[i] = this.env ? this.env.groundAt(g.x, g.z) : 0;
      h.gx[i] = g.x; h.gz[i] = g.z;
    }
    return h.gy[i];
  }

  private ensureAnchor(px: number, py: number, pz: number): void {
    if (!this.anchor) {
      this.anchor = new THREE.Group();
      this.anchor.name = 'ambient-herds';
      this.scene!.add(this.anchor);
      this.anchorX = Number.NaN;
    }
    if (!(Math.hypot(px - this.anchorX, pz - this.anchorZ) < REANCHOR_M)) {
      // THE FRAME: instance matrices are rewritten every frame relative to the anchor, so re-seating it is free
      this.anchorX = px; this.anchorY = py; this.anchorZ = pz;
      this.anchor.position.set(px, py, pz);
      this.anchor.updateMatrixWorld(true);
    }
  }

  // ─── bodies ────────────────────────────────────────────────────────────────
  private catalogId(prefabKey: string): bigint | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try { for (const pm of (conn.db.prefabModel as unknown as { iter: () => Iterable<{ prefabKey: string; assetId: bigint }> }).iter()) if (pm.prefabKey === prefabKey) return pm.assetId; } catch { /* churn */ }
    return null;
  }

  private batchFor(species: HerdSpeciesKey, lod: 1 | 2): CreatureBatch | null {
    const key = `${species}:${lod}`;
    let slot = this.models.get(key);
    if (!slot) { slot = { batch: null, loading: false, failed: 0 }; this.models.set(key, slot); }
    if (slot.batch) return slot.batch;
    if (!slot.loading && slot.failed < 3 && this.assets && this.anchor && performance.now() >= (slot.retryAt ?? 0)) void this.loadModel(species, lod, slot);
    return null;
  }

  private async loadModel(species: HerdSpeciesKey, lod: 1 | 2, slot: ModelSlot): Promise<void> {
    const id = this.catalogId(`prop:herd_${species}`);
    // A body not in the catalogue (a shard without the prop, or the catalogue still on the wire) was looked up for every
    // animal every frame — an async call and a whole-table scan each. Ask again in a second.
    if (id === null || !this.assets || !this.anchor) { slot.retryAt = performance.now() + 1000; return; }
    slot.loading = true;
    try {
      const { object, release } = await this.assets.loadRepresentation(id, lod, { urgent: true }); // THE QUEUE LAW: a creature in view needs its body now
      if (this.disposed || !this.anchor) { release(); return; } // a load that lands after dispose() must not re-add a mesh
      const parts = creatureParts(object, 'quadruped', { grazeNeck: HERD_SPECIES[species].grazeNeck });
      if (!parts) { release(); slot.failed++; return; }
      slot.batch = new CreatureBatch(this.anchor, parts, HERD.maxAnimals, `herd:${species}:lod${lod}`, release);
    } catch { slot.failed++; }
    finally { slot.loading = false; }
  }

  /**
   * THE QUEUE LAW (2026-09-29, the browser probe): AssetModule's load gate is a FIFO shared with the whole world's
   * streaming, so a body asked for only when a herd first comes into range waits behind every terrain, foliage and
   * settlement load queued before it (three tries of the probe's herd leg lost that race). The far bodies (LOD2,
   * ~1.5 MB each) are asked for the moment the layer is live; the near LOD1 still loads on demand.
   */
  private prefetch(): void {
    if (this.prefetched || !this.assets || !this.anchor) return;
    this.prefetched = true;
    for (const sp of HERD_SPECIES_KEYS) this.batchFor(sp, 2);
  }

  private updateHooves(gain: number, at: { x: number; y: number; z: number } | null): void {
    if (gain > 0.01 && at) {
      if (!this.hooves) this.hooves = sfxLoop('ambient_herd_hooves', SCOPE);
      this.hooves.at(at);
      this.hooves.set(gain, 1, 0.4);
    } else if (this.hooves) this.hooves.set(0, 1, 1.2);
  }

  // ─── levers ────────────────────────────────────────────────────────────────
  private spawnNear(species: HerdSpeciesKey, dist: number, side: number, tame: boolean): Record<string, unknown> | null {
    if (!HERD_SPECIES_KEYS.includes(species) || !this.seed) return null;
    // ahead of the viewer: three's camera forward is (−sin yaw, −cos yaw), its right (cos yaw, −sin yaw). A herd is
    // never set down in water: the first of 16 headings from the view whose spot is dry ground wins (the probe found
    // its "quiet spot" beside a wetland looking out to sea, and three herds grazing on the sea bed).
    let yaw = atlas.localYaw ?? 0, x = 0, z = 0;
    for (let k = 0; k < 16; k++) {
      const y = (atlas.localYaw ?? 0) + (k % 2 ? 1 : -1) * Math.ceil(k / 2) * (Math.PI / 8);
      const tx = atlas.localX - Math.sin(y) * dist + Math.cos(y) * side, tz = atlas.localZ - Math.cos(y) * dist - Math.sin(y) * side;
      const g = this.groundOf(tx, tz);
      yaw = y; x = tx; z = tz;
      if (g.aboveSea > 1 && !g.wet) break;
    }
    const cx = Math.floor(x / HERD.cell), cz = Math.floor(z / HERD.cell);
    const seed = (Math.floor(Math.random() * 0xffffffff)) >>> 0;
    // a forced herd stands where it was put: an INFINITE loop period (t / ∞ = 0) with the phases that zero both terms.
    // WHY: a 1e9 s period was not "still" — the wall clock is ~1.79e9 s, so the loop sat at an arbitrary phase and the
    // herd landed up to 140 m from the chosen spot (the probe's 60 m "wild" herd never came inside its flee radius).
    const site: HerdSite = { id: `forced:${cx}:${cz}:${seed}`, cx, cz, homeX: x, homeZ: z, seed, count: 0.7, pA: Infinity, pB: Infinity, phA: -Math.PI / 2, phB: 0 };
    const h = this.makeHerd(site, species, true, tame);
    return { id: site.id, species, count: h.count, x: Math.round(x), z: Math.round(z), tame, yaw };
  }

  private findNearest(maxKm: number): Record<string, unknown> | null {
    if (!this.seed) return null;
    const px = atlas.localX, pz = atlas.localZ;
    const maxR = Math.ceil((maxKm * 1000) / HERD.cell);
    const ox = Math.floor(px / HERD.cell), oz = Math.floor(pz / HERD.cell);
    for (let r = 0; r <= maxR; r++) {
      let best: { h: Herd; d: number } | null = null;
      for (let dx = -r; dx <= r; dx++) for (let dz = -r; dz <= r; dz++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
        const h = this.judgeCell(ox + dx, oz + dz);
        if (!h) continue;
        const d = Math.hypot(h.site.homeX - px, h.site.homeZ - pz);
        if (!best || d < best.d) best = { h, d };
      }
      if (best) {
        const c = herdCentre(best.h.site, Date.now() / 1000);
        return { id: best.h.site.id, species: best.h.species, count: best.h.count, x: Math.round(c.x), z: Math.round(c.z), km: +(best.d / 1000).toFixed(2) };
      }
    }
    return null;
  }

  private diag(): Record<string, unknown> {
    const reasons: Record<string, number> = {};
    for (const why of this.rejected.values()) reasons[why] = (reasons[why] ?? 0) + 1;
    return {
      gate: this.gate, cap: herdAnimalCap(activePreset().particleCap ?? 0), range: Math.round(this.drawRange()),
      herds: [...this.herds.values()].map((h) => {
        const states: Record<string, number> = {};
        for (const a of h.animals) states[a.state] = (states[a.state] ?? 0) + 1;
        return { id: h.site.id, species: h.species, count: h.count, dist: Math.round(h.dist), fleeing: h.flight.fleeing, flight: Math.round(Math.hypot(h.flight.ox, h.flight.oz)), states, forced: h.forced, tame: h.tame };
      }),
      models: Object.fromEntries([...this.models].map(([k, s]) => [k, s.batch ? `ready (${s.batch.count} drawn)` : s.loading ? 'loading' : s.failed ? `failed ×${s.failed}` : 'waiting'])),
      rejected: reasons,
      stats: { ...this.stats },
      // THE QUEUE LAW's evidence: the shared load gate's lanes in use and loads waiting (AssetModule internals, read-only)
      assetGate: this.assets ? { active: (this.assets as unknown as { gateActive: number }).gateActive, queued: (this.assets as unknown as { gateQueue: unknown[] }).gateQueue?.length ?? null } : null,
    };
  }

  private clearHerds(): void {
    this.herds.clear();
    for (const slot of this.models.values()) { slot.batch?.begin(); slot.batch?.end(true); }
    if (this.hooves) { stopSfxScope(SCOPE); this.hooves = null; }
  }

  dispose(): void {
    this.disposed = true;
    this.clearHerds();
    for (const slot of this.models.values()) slot.batch?.dispose();
    this.models.clear();
    this.anchor?.removeFromParent();
    this.anchor = null;
  }
}
